import React, { memo } from "react";
import type { FC, MouseEventHandler } from "react";
import { Box } from "@mui/system";
import { IconButton, Stack, Typography } from "@mui/material";
import { Track } from "store/track/track.model";
import { ReactComponent as MenuSVG } from "assets/images/more.svg";
import { ReactComponent as PlayingSVG } from "assets/images/playing.svg";
import { ReactComponent as SdCardSVG } from "assets/images/sd-card.svg";
import { formattedSeconds } from "utils/time";
import { useShallowPickSelector } from "store/useSelector";
import { imageUrlOf } from "utils/imageUrl";
import { LazyLoadImage } from "react-lazy-load-image-component";

interface TrackItemProps {
  track: Track;
  onClick?: MouseEventHandler;
  onClickMenu?: MouseEventHandler;
  withDuration?: boolean;
  isPlaying?: boolean;
}

export const TrackItem: FC<TrackItemProps> = memo(
  ({
    track,
    onClick,
    onClickMenu,
    withDuration = false,
    isPlaying = false,
  }) => {
    const { id, name, coverLink, artistName, durationMs } = track;

    const { tracks: archivedTracks } = useShallowPickSelector("archive", [
      "tracks",
    ]);
    const isArchived = !!archivedTracks.find((t) => t.id === id);
    return (
      <Stack
        direction={"row"}
        alignItems={"center"}
        pr={2}
        pl={1}
        py={1}
        sx={{ cursor: "pointer" }}
        onClick={onClick}
      >
        {onClickMenu && (
          <IconButton onClick={onClickMenu}>
            <MenuSVG opacity={0.3} />
          </IconButton>
        )}
        {withDuration && (
          <Typography
            fontSize={"0.75rem"}
            fontFamily={"Inter"}
            fontWeight={500}
            color={isPlaying ? "#fff" : "rgba(255,255,255,0.4)"}
            ml={2}
          >
            {formattedSeconds(durationMs ? durationMs % 1000 : 0)}
          </Typography>
        )}
        <Stack
          flex={1}
          mr={2}
          overflow={"hidden"}
          textOverflow={"ellipsis"}
          whiteSpace={"nowrap"}
          alignItems={"flex-end"}
        >
          <Typography
            fontSize={"0.875rem"}
            fontFamily={"Inter"}
            fontWeight={500}
            color={isPlaying ? "#fff" : "rgba(255,255,255,0.9)"}
            textAlign={"right"}
            pl={1}
          >
            {name}
          </Typography>
          <Stack direction={"row"}>
            <Typography
              fontSize={"0.75rem"}
              fontFamily={"Inter"}
              fontWeight={500}
              color={isPlaying ? "#fff" : "rgba(255,255,255,0.4)"}
              textAlign={"right"}
              pl={1}
              pr={isArchived ? 0.5 : 0}
            >
              {artistName}
            </Typography>
            {isArchived && <SdCardSVG opacity={0.4} />}
          </Stack>
        </Stack>
        <Box width={48} height={48} borderRadius={"5px"} position={"relative"}>
          <LazyLoadImage
            src={imageUrlOf(track)}
            height={"100%"}
            width={"100%"}
            style={{ borderRadius: "5px", opacity: isPlaying ? 0.5 : 1 }}
            alt=""
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = track?.coverLink || '';
            }}
          />
          {isPlaying && (
            <Stack
              position={"absolute"}
              width={"100%"}
              height={"100%"}
              top={0}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <PlayingSVG />
            </Stack>
          )}
        </Box>
      </Stack>
    );
  },
  (prevProps, nextProps) =>
    prevProps.track.id === nextProps.track.id &&
    prevProps.isPlaying === nextProps.isPlaying
);
